import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App.js';
import { 
  Music, Upload, Download, LogOut, Settings, Shield, 
  Coins, CheckCircle, Clock, AlertCircle, X, Eye, Edit3,
  Trash2, RefreshCw, FileText, ChevronDown, Search, Sparkles, Zap, Mic,
} from 'lucide-react';
import FileUploader from '../components/FileUploader.js';
import TrackDetailsModal from '../components/TrackDetailsModal.js';
import TrackEditModal from '../components/TrackEditModal.js';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  queued: { label: 'Queued', color: 'status-queued', icon: Clock, animate: false },
  pending_analysis: { label: 'Queued', color: 'status-queued', icon: Clock, animate: false },
  uploading: { label: 'Uploading', color: 'status-uploading', icon: Upload, animate: true },
  uploading_to_services: { label: 'Uploading', color: 'status-uploading', icon: Upload, animate: true },
  transcribing: { label: 'Transcribing', color: 'status-transcribing', icon: FileText, animate: true },
  analyzing: { label: 'Analyzing', color: 'status-analyzing', icon: Music, animate: true },
  cleaning: { label: 'Processing', color: 'status-cleaning', icon: Settings, animate: true },
  describing: { label: 'Generating', color: 'status-describing', icon: Edit3, animate: true },
  complete: { label: 'Complete', color: 'status-complete', icon: CheckCircle, animate: false },
  error: { label: 'Error', color: 'status-error', icon: AlertCircle, animate: false },
  cyanite_upload_failed: { label: 'Retry', color: 'status-error', icon: AlertCircle, animate: false },
  cyanite_analysis_failed: { label: 'Retry', color: 'status-error', icon: AlertCircle, animate: false }
};

// Audio Wave Component
const AudioWave = () => (
  <div className="audio-wave">
    <span></span>
    <span></span>
    <span></span>
    <span></span>
    <span></span>
  </div>
);

// Floating Particles
const Particles = () => (
  <div className="particles">
    {[...Array(9)].map((_, i) => (
      <div key={i} className="particle" />
    ))}
  </div>
);

export default function Dashboard() {
  const { user, token, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [editingTrack, setEditingTrack] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Folders state
  const [folders, setFolders] = useState([]);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const [activeFolderId, setActiveFolderId] = useState('all'); // 'all' | 'uncategorized' | <id>
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderEditing, setFolderEditing] = useState(null); // null=create, otherwise folder being edited
  const [showMoveMenu, setShowMoveMenu] = useState(false);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFolders(data.folders || []);
        setUncategorizedCount(data.uncategorized_count || 0);
      }
    } catch (e) {
      console.error('Failed to fetch folders:', e);
    }
  }, [token]);

  const fetchTracks = useCallback(async () => {
    try {
      const qs = activeFolderId === 'all' ? '' : `?folder_id=${activeFolderId}`;
      const response = await fetch(`${API_URL}/api/tracks${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Skip state update if nothing relevant changed — avoids needless re-renders
        // during polling. Compare a cheap signature (id + status + updated_at).
        setTracks(prev => {
          const sig = (arr) => arr.map(t => `${t.id}:${t.status}:${t.updated_at || ''}`).join('|');
          if (sig(prev) === sig(data)) return prev;
          return data;
        });
      }
    } catch (error) {
      console.error('Failed to fetch tracks:', error);
    }
    setLoading(false);
  }, [token, activeFolderId]);

  // Initial load
  useEffect(() => {
    fetchTracks();
    fetchFolders();
  }, [fetchTracks, fetchFolders]);

  // Smart polling: only poll while tracks are actively processing.
  // Non-terminal statuses = anything that can still transition (uploading/analyzing/
  // cleaning/describing/etc). Once everything is complete/error, stop polling entirely
  // to eliminate idle network traffic and re-renders.
  const TERMINAL_STATUSES = ['complete', 'error', 'queued', 'cyanite_upload_failed'];
  const hasActiveWork = tracks.some(t => !TERMINAL_STATUSES.includes(t.status));
  useEffect(() => {
    if (!hasActiveWork) return;
    const interval = setInterval(fetchTracks, 5000); // faster cadence while work is in flight
    return () => clearInterval(interval);
  }, [hasActiveWork, fetchTracks]);

  const handleUploadComplete = () => {
    fetchTracks();
    fetchFolders();
    refreshUser();
  };

  // Folder CRUD
  const saveFolder = async ({ name, color }) => {
    const isEdit = !!folderEditing;
    const url = isEdit ? `${API_URL}/api/folders/${folderEditing.id}` : `${API_URL}/api/folders`;
    const method = isEdit ? 'PUT' : 'POST';
    const res = await fetch(url, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, color })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to save folder');
    }
    await fetchFolders();
    setShowFolderModal(false);
    setFolderEditing(null);
  };

  const deleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Its tracks will become Uncategorized.')) return;
    const res = await fetch(`${API_URL}/api/folders/${folderId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res.ok) {
      if (activeFolderId === folderId) setActiveFolderId('all');
      await fetchFolders();
      await fetchTracks();
    }
  };

  // Bulk-move selected tracks to a folder (or unassign if folderId === null)
  const bulkMoveToFolder = async (folderId) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    const res = await fetch(`${API_URL}/api/tracks/bulk-folder`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ track_ids: ids, folder_id: folderId })
    });
    if (res.ok) {
      setSelectedIds(new Set());
      setShowMoveMenu(false);
      await fetchTracks();
      await fetchFolders();
    }
  };

  const handleDeleteTrack = async (trackId) => {
    if (!window.confirm('Are you sure you want to delete this track?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/tracks/${trackId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setTracks(tracks.filter(t => t.id !== trackId));
        if (selectedTrack?.id === trackId) setSelectedTrack(null);
        if (editingTrack?.id === trackId) setEditingTrack(null);
      }
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleRetryTrack = async (trackId) => {
    try {
      const response = await fetch(`${API_URL}/api/tracks/${trackId}/retry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        fetchTracks();
      }
    } catch (error) {
      console.error('Retry failed:', error);
    }
  };

  const handleTrackAction = async (trackId, action) => {
    // action: 'generate-description' (1 credit) or 'transcribe-lyrics' (1 credit)
    try {
      const res = await fetch(`${API_URL}/api/tracks/${trackId}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchTracks();
        return;
      }
      if (res.status === 402) {
        alert('Insufficient credits — top up to continue.');
      } else if (res.status === 409) {
        const txt = await res.text();
        let msg = 'Track is still processing — please wait.';
        try { msg = JSON.parse(txt).detail || msg; } catch {}
        alert(msg);
      } else {
        alert(`Action failed (HTTP ${res.status}).`);
      }
    } catch (e) {
      console.error(`${action} failed:`, e);
      alert('Network error — please try again.');
    }
  };

  const handleExport = async (format) => {
    try {
      // If user has selected specific tracks, export ONLY those (completed ones only).
      // Otherwise export all completed tracks.
      const completedSelected = [...selectedIds].filter(id => 
        tracks.find(t => t.id === id && t.status === 'complete')
      );
      const qs = completedSelected.length > 0 ? `?track_ids=${completedSelected.join(',')}` : '';
      const response = await fetch(`${API_URL}/api/tracks/export/${format}${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const suffix = completedSelected.length > 0 ? `-selected-${completedSelected.length}` : '';
        
        if (format === 'csv') {
          const blob = new Blob([data.csv], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `syncpulse-export${suffix}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        } else {
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `syncpulse-export${suffix}.json`;
          a.click();
          URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
    setShowExportMenu(false);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    const visibleCompleteIds = filteredTracks.filter(t => t.status === 'complete').map(t => t.id);
    const allSelected = visibleCompleteIds.length > 0 && visibleCompleteIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        visibleCompleteIds.forEach(id => next.delete(id));
      } else {
        visibleCompleteIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const filteredTracks = tracks.filter(track => {
    const matchesSearch = track.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
      track.final_metadata?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      track.final_metadata?.artist?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || track.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: tracks.length,
    complete: tracks.filter(t => t.status === 'complete').length,
    processing: tracks.filter(t => !['complete', 'error'].includes(t.status)).length,
    errors: tracks.filter(t => t.status === 'error').length
  };

  return (
    <div className="min-h-screen gradient-bg relative">
      <Particles />
      
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 animate-fadeInUp">
            <img 
              src="https://customer-assets.emergentagent.com/job_syncpulse-preview/artifacts/4drbfpzf_SyncitLogoWhite.png" 
              alt="SyncPulse" 
              className="h-20 w-auto object-contain"
            />
          </div>

          <div className="flex items-center gap-4">
            <div className="credits-badge flex items-center gap-2 px-4 py-2 rounded-xl animate-fadeInUp stagger-1">
              <Coins className="w-4 h-4 text-primary" style={{color: '#F96F51'}} />
              <span className="font-bold text-white" data-testid="credits-display">{user?.credits || 0}</span>
              <span className="text-sm text-white/60">credits</span>
            </div>

            {user?.is_admin && (
              <button
                onClick={() => navigate('/admin')}
                className="p-2.5 glass-card rounded-xl animate-fadeInUp stagger-2 glow-primary-hover"
                data-testid="admin-link"
              >
                <Shield className="w-5 h-5 text-white/70" />
              </button>
            )}

            <button
              onClick={logout}
              className="p-2.5 glass-card rounded-xl animate-fadeInUp stagger-3 hover:bg-red-500/20 transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5 text-white/70" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 relative z-10">
        {/* Welcome Section */}
        <div className="mb-8 animate-fadeInUp">
          <h2 className="font-heading text-3xl font-bold text-white mb-2">
            Welcome back, <span className="gradient-text">{user?.name?.split(' ')[0]}</span>
          </h2>
          <p className="text-white/60">
            Upload audio tracks to generate AI-powered metadata and sync descriptions.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="stat-card glass-card p-5 rounded-2xl animate-fadeInUp stagger-1">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-white/50">Total Tracks</span>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center" style={{background: 'linear-gradient(135deg, rgba(249,111,81,0.2), rgba(249,111,81,0.05))'}}>
                <Music className="w-5 h-5" style={{color: '#F96F51'}} />
              </div>
            </div>
            <p className="font-heading text-4xl font-bold text-white" data-testid="total-tracks">{stats.total}</p>
          </div>

          <div className="stat-card glass-card p-5 rounded-2xl animate-fadeInUp stagger-2">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-white/50">Completed</span>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500/20 to-green-500/5 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <p className="font-heading text-4xl font-bold text-white" data-testid="completed-tracks">{stats.complete}</p>
          </div>

          <div className="stat-card glass-card p-5 rounded-2xl animate-fadeInUp stagger-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-white/50">Processing</span>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-500/5 flex items-center justify-center">
                {stats.processing > 0 ? <AudioWave /> : <Clock className="w-5 h-5 text-amber-400" />}
              </div>
            </div>
            <p className="font-heading text-4xl font-bold text-white" data-testid="processing-tracks">{stats.processing}</p>
          </div>

          <div className="stat-card glass-card p-5 rounded-2xl animate-fadeInUp stagger-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-white/50">Errors</span>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500/20 to-red-500/5 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
            </div>
            <p className="font-heading text-4xl font-bold text-white" data-testid="error-tracks">{stats.errors}</p>
          </div>
        </div>

        {/* Upload Section */}
        <div className="animate-fadeInUp stagger-5">
          <FileUploader 
            token={token} 
            onUploadComplete={handleUploadComplete}
            folders={folders}
            activeFolderId={activeFolderId}
          />
        </div>

        {/* Folders Bar */}
        <div className="mt-8 flex items-center gap-2 overflow-x-auto pb-2" data-testid="folders-bar">
          <button
            onClick={() => setActiveFolderId('all')}
            className={`shrink-0 px-4 py-2 rounded-xl text-sm transition-colors border ${activeFolderId === 'all' ? 'bg-primary/20 border-primary/40 text-white' : 'glass-card border-white/10 text-white/70 hover:bg-white/10'}`}
            style={activeFolderId === 'all' ? {backgroundColor: 'rgba(249,111,81,0.2)', borderColor: 'rgba(249,111,81,0.4)'} : {}}
            data-testid="folder-filter-all"
          >
            All tracks ({folders.reduce((a, f) => a + f.track_count, 0) + uncategorizedCount})
          </button>
          <button
            onClick={() => setActiveFolderId('uncategorized')}
            className={`shrink-0 px-4 py-2 rounded-xl text-sm transition-colors border ${activeFolderId === 'uncategorized' ? 'bg-primary/20 border-primary/40 text-white' : 'glass-card border-white/10 text-white/70 hover:bg-white/10'}`}
            style={activeFolderId === 'uncategorized' ? {backgroundColor: 'rgba(249,111,81,0.2)', borderColor: 'rgba(249,111,81,0.4)'} : {}}
            data-testid="folder-filter-uncategorized"
          >
            Uncategorized ({uncategorizedCount})
          </button>
          {folders.map(f => (
            <button
              key={f.id}
              onClick={() => setActiveFolderId(f.id)}
              className={`shrink-0 px-4 py-2 rounded-xl text-sm transition-colors border flex items-center gap-2 group ${activeFolderId === f.id ? 'bg-primary/20 border-primary/40 text-white' : 'glass-card border-white/10 text-white/70 hover:bg-white/10'}`}
              style={activeFolderId === f.id ? {backgroundColor: 'rgba(249,111,81,0.2)', borderColor: 'rgba(249,111,81,0.4)'} : {}}
              data-testid={`folder-filter-${f.id}`}
            >
              <span className="w-2 h-2 rounded-full" style={{background: f.color || '#F96F51'}} />
              <span>{f.name} ({f.track_count})</span>
              <span
                role="button"
                onClick={(e) => { e.stopPropagation(); setFolderEditing(f); setShowFolderModal(true); }}
                className="opacity-0 group-hover:opacity-70 hover:!opacity-100 text-xs"
                title="Edit"
              >✎</span>
              <span
                role="button"
                onClick={(e) => { e.stopPropagation(); deleteFolder(f.id); }}
                className="opacity-0 group-hover:opacity-70 hover:!opacity-100 text-xs text-red-300"
                title="Delete"
              >✕</span>
            </button>
          ))}
          <button
            onClick={() => { setFolderEditing(null); setShowFolderModal(true); }}
            className="shrink-0 px-4 py-2 rounded-xl text-sm glass-card border border-dashed border-white/20 text-white/60 hover:text-white hover:border-white/40 transition-colors"
            data-testid="new-folder-button"
          >
            + New Folder
          </button>
        </div>

        {/* Track List */}
        <div className="mt-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <h3 className="font-heading text-2xl font-bold text-white flex items-center gap-2">
              <Sparkles className="w-6 h-6" style={{color: '#F96F51'}} />
              Your Tracks
            </h3>
            
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="text"
                  placeholder="Search tracks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2.5 input-dark rounded-xl w-full sm:w-64"
                  data-testid="search-input"
                />
              </div>

              {/* Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2.5 input-dark rounded-xl appearance-none cursor-pointer"
                data-testid="status-filter"
              >
                <option value="all">All Status</option>
                <option value="complete">Complete</option>
                <option value="error">Errors</option>
                <option value="analyzing">Processing</option>
              </select>

              {/* Move to folder (appears when tracks selected) */}
              {selectedIds.size > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setShowMoveMenu(!showMoveMenu)}
                    className="flex items-center gap-2 px-4 py-2.5 glass-card rounded-xl hover:bg-white/10 transition-colors border border-primary/30"
                    style={{borderColor: 'rgba(249,111,81,0.3)'}}
                    data-testid="move-to-button"
                  >
                    <span className="text-white">Move ({selectedIds.size})</span>
                    <ChevronDown className="w-4 h-4 text-white/70" />
                  </button>
                  {showMoveMenu && (
                    <div className="absolute right-0 mt-2 w-64 glass-card rounded-xl shadow-2xl overflow-hidden z-50 border border-white/10 max-h-80 overflow-y-auto">
                      <div className="px-4 py-2 text-xs text-white/50 bg-white/5 border-b border-white/10">
                        Move {selectedIds.size} track(s) to…
                      </div>
                      <button
                        onClick={() => bulkMoveToFolder(null)}
                        className="w-full px-4 py-3 text-left text-white/80 hover:bg-white/10 transition-colors text-sm"
                        data-testid="move-to-uncategorized"
                      >
                        Uncategorized
                      </button>
                      {folders.map(f => (
                        <button
                          key={f.id}
                          onClick={() => bulkMoveToFolder(f.id)}
                          className="w-full px-4 py-3 text-left text-white/80 hover:bg-white/10 transition-colors text-sm flex items-center gap-2"
                          data-testid={`move-to-${f.id}`}
                        >
                          <span className="w-2 h-2 rounded-full" style={{background: f.color || '#F96F51'}} />
                          <span>{f.name}</span>
                        </button>
                      ))}
                      {folders.length === 0 && (
                        <div className="px-4 py-3 text-sm text-white/40">No folders yet. Create one first.</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Export */}
              <div className="relative">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="flex items-center gap-2 px-4 py-2.5 glass-card rounded-xl hover:bg-white/10 transition-colors"
                  data-testid="export-button"
                >
                  <Download className="w-4 h-4 text-white/70" />
                  <span className="text-white/70">
                    {selectedIds.size > 0 ? `Export (${[...selectedIds].filter(id => tracks.find(t => t.id === id && t.status === 'complete')).length})` : 'Export'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-white/70" />
                </button>
                
                {showExportMenu && (
                  <div className="absolute right-0 mt-2 w-56 glass-card rounded-xl shadow-2xl overflow-hidden z-50 border border-white/10">
                    <div className="px-4 py-2 text-xs text-white/50 bg-white/5 border-b border-white/10">
                      {selectedIds.size > 0 ? `${[...selectedIds].filter(id => tracks.find(t => t.id === id && t.status === 'complete')).length} selected track(s)` : 'All completed tracks'}
                    </div>
                    <button
                      onClick={() => handleExport('csv')}
                      className="w-full px-4 py-3 text-left text-white/80 hover:bg-white/10 transition-colors"
                      data-testid="export-csv"
                    >
                      Export CSV
                    </button>
                    <button
                      onClick={() => handleExport('json')}
                      className="w-full px-4 py-3 text-left text-white/80 hover:bg-white/10 transition-colors"
                      data-testid="export-json"
                    >
                      Export JSON
                    </button>
                    {selectedIds.size > 0 && (
                      <button
                        onClick={() => { clearSelection(); setShowExportMenu(false); }}
                        className="w-full px-4 py-3 text-left text-white/50 hover:bg-white/10 transition-colors border-t border-white/10 text-sm"
                        data-testid="clear-selection"
                      >
                        Clear selection
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tracks Table */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" style={{borderTopColor: '#F96F51'}}></div>
            </div>
          ) : filteredTracks.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center animate-fadeInScale">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center" style={{background: 'linear-gradient(135deg, rgba(249,111,81,0.2), rgba(249,111,81,0.05))'}}>
                <Music className="w-10 h-10" style={{color: '#F96F51'}} />
              </div>
              <h4 className="font-heading text-xl font-bold text-white mb-2">No tracks yet</h4>
              <p className="text-white/50">Upload your first audio file to get started</p>
            </div>
          ) : (
            <div className="glass-card rounded-2xl overflow-hidden border border-white/10">
              <div className="overflow-x-auto">
                <table className="w-full table-dark" data-testid="tracks-table">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="px-4 py-4 w-10">
                        <input
                          type="checkbox"
                          checked={(() => {
                            const visibleComplete = filteredTracks.filter(t => t.status === 'complete');
                            return visibleComplete.length > 0 && visibleComplete.every(t => selectedIds.has(t.id));
                          })()}
                          onChange={toggleSelectAllVisible}
                          className="w-4 h-4 rounded border-white/20 bg-white/5 cursor-pointer accent-primary"
                          style={{accentColor: '#F96F51'}}
                          data-testid="select-all-tracks"
                          title="Select all completed tracks"
                        />
                      </th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">Track</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">Status</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider hidden md:table-cell">Genre</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider hidden lg:table-cell">Tempo</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider hidden lg:table-cell">Key</th>
                      <th className="text-right px-6 py-4 text-xs font-semibold text-white/40 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredTracks.map((track, index) => {
                      const statusConfig = STATUS_CONFIG[track.status] || STATUS_CONFIG.error;
                      const StatusIcon = statusConfig.icon;
                      const meta = track.final_metadata || {};

                      return (
                        <tr 
                          key={track.id} 
                          className={`hover:bg-white/5 transition-all duration-300 ${selectedIds.has(track.id) ? 'bg-primary/5' : ''}`}
                          data-testid={`track-row-${track.id}`}
                          style={{ animationDelay: `${index * 0.05}s` }}
                        >
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(track.id)}
                              onChange={() => toggleSelect(track.id)}
                              disabled={track.status !== 'complete'}
                              className="w-4 h-4 rounded border-white/20 bg-white/5 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                              style={{accentColor: '#F96F51'}}
                              data-testid={`select-track-${track.id}`}
                              title={track.status === 'complete' ? 'Select for export' : 'Only completed tracks can be exported'}
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-white truncate max-w-xs">
                                {meta.title || track.filename}
                              </p>
                              <div className="flex items-center gap-2 flex-wrap">
                                {meta.artist && (
                                  <p className="text-sm text-white/50 truncate max-w-[200px]">{meta.artist}</p>
                                )}
                                {(() => {
                                  const f = folders.find(fl => fl.id === track.folder_id);
                                  if (!f) return null;
                                  return (
                                    <span
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] text-white/80 border border-white/10"
                                      style={{ background: 'rgba(255,255,255,0.05)' }}
                                      data-testid={`track-folder-chip-${track.id}`}
                                    >
                                      <span className="w-1.5 h-1.5 rounded-full" style={{background: f.color || '#F96F51'}} />
                                      {f.name}
                                    </span>
                                  );
                                })()}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white ${statusConfig.color} ${statusConfig.animate ? 'animate-pulse' : ''}`}>
                              <StatusIcon className={`w-3.5 h-3.5 ${statusConfig.animate ? 'animate-spin' : ''}`} />
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 hidden md:table-cell">
                            <span className="text-sm text-white/70">
                              {meta.genre?.slice(0, 2).join(', ') || '—'}
                            </span>
                          </td>
                          <td className="px-6 py-4 hidden lg:table-cell">
                            <span className="text-sm font-mono text-white/70">
                              {meta.tempoBPM ? `${Math.round(meta.tempoBPM)} BPM` : '—'}
                            </span>
                          </td>
                          <td className="px-6 py-4 hidden lg:table-cell">
                            <span className="text-sm font-mono text-white/70">{meta.key || '—'}</span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end gap-2">
                              {track.status === 'complete' && (
                                <>
                                  <button
                                    onClick={() => setSelectedTrack(track)}
                                    className="p-2 rounded-lg hover:bg-white/10 transition-colors group"
                                    title="View Details"
                                    data-testid={`view-track-${track.id}`}
                                  >
                                    <Eye className="w-4 h-4 text-white/50 group-hover:text-white transition-colors" />
                                  </button>
                                  <button
                                    onClick={() => setEditingTrack(track)}
                                    className="p-2 rounded-lg hover:bg-white/10 transition-colors group"
                                    title="Edit"
                                    data-testid={`edit-track-${track.id}`}
                                  >
                                    <Edit3 className="w-4 h-4 text-white/50 group-hover:text-white transition-colors" />
                                  </button>
                                  {(() => {
                                    const hasDesc = !!meta.description;
                                    const hasLyrics = !!track.lyrics_present;
                                    const lyricsAware = !!track.description_used_lyrics;
                                    // Show Sparkles when:
                                    //  - no description yet (generate), OR
                                    //  - lyrics arrived AFTER the description (regenerate to quote them)
                                    const show = !hasDesc || (hasLyrics && !lyricsAware);
                                    if (!show) return null;
                                    const tooltip = !hasDesc
                                      ? 'Generate sync description (1 credit)'
                                      : 'Regenerate description with lyrics (1 credit)';
                                    return (
                                      <button
                                        onClick={() => handleTrackAction(track.id, 'generate-description')}
                                        className="p-2 rounded-lg hover:bg-primary/20 transition-colors group relative"
                                        title={tooltip}
                                        data-testid={`generate-description-${track.id}`}
                                      >
                                        <Sparkles className="w-4 h-4 text-primary/70 group-hover:text-primary transition-colors" />
                                        {hasDesc && hasLyrics && !lyricsAware && (
                                          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-400 rounded-full" />
                                        )}
                                      </button>
                                    );
                                  })()}
                                  {!track.lyrics_present && (
                                    <button
                                      onClick={() => handleTrackAction(track.id, 'transcribe-lyrics')}
                                      className="p-2 rounded-lg hover:bg-primary/20 transition-colors group"
                                      title="Transcribe lyrics (1 credit)"
                                      data-testid={`transcribe-lyrics-${track.id}`}
                                    >
                                      <Mic className="w-4 h-4 text-primary/70 group-hover:text-primary transition-colors" />
                                    </button>
                                  )}
                                </>
                              )}
                              {track.status === 'error' && (
                                <button
                                  onClick={() => handleRetryTrack(track.id)}
                                  className="p-2 rounded-lg hover:bg-amber-500/20 transition-colors"
                                  title="Retry"
                                  data-testid={`retry-track-${track.id}`}
                                >
                                  <RefreshCw className="w-4 h-4 text-amber-400" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteTrack(track.id)}
                                className="p-2 rounded-lg hover:bg-red-500/20 transition-colors"
                                title="Delete"
                                data-testid={`delete-track-${track.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-400" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Modals */}
      {selectedTrack && (
        <TrackDetailsModal
          track={selectedTrack}
          token={token}
          onClose={() => setSelectedTrack(null)}
          onEdit={() => {
            setEditingTrack(selectedTrack);
            setSelectedTrack(null);
          }}
        />
      )}

      {editingTrack && (
        <TrackEditModal
          track={editingTrack}
          token={token}
          onClose={() => setEditingTrack(null)}
          onSave={() => {
            setEditingTrack(null);
            fetchTracks();
          }}
        />
      )}

      {/* Folder Create/Edit Modal */}
      {showFolderModal && (
        <FolderModal
          folder={folderEditing}
          onSave={saveFolder}
          onClose={() => { setShowFolderModal(false); setFolderEditing(null); }}
        />
      )}
    </div>
  );
}

// Lightweight inline modal for creating / editing a folder
function FolderModal({ folder, onSave, onClose }) {
  const [name, setName] = useState(folder?.name || '');
  const [color, setColor] = useState(folder?.color || '#F96F51');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const PRESET_COLORS = ['#F96F51', '#00C2FF', '#9B59F9', '#22C55E', '#F4C20D', '#FF6EB4', '#8E9196'];

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({ name: name.trim(), color });
    } catch (e) {
      setError(e.message || 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="glass-card rounded-2xl p-6 w-full max-w-md border border-white/10"
        onClick={(e) => e.stopPropagation()}
        data-testid="folder-modal"
      >
        <h3 className="text-xl font-bold text-white mb-4">{folder ? 'Edit Folder' : 'New Folder'}</h3>
        <label className="block text-xs text-white/50 mb-1">Name</label>
        <input
          type="text"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Cinematic Cues"
          className="w-full px-4 py-3 input-dark rounded-xl mb-4"
          data-testid="folder-name-input"
          autoFocus
        />
        <label className="block text-xs text-white/50 mb-2">Color</label>
        <div className="flex gap-2 mb-4">
          {PRESET_COLORS.map(c => (
            <button
              key={c}
              onClick={() => setColor(c)}
              className={`w-8 h-8 rounded-full border-2 transition-transform ${color === c ? 'scale-110 border-white' : 'border-transparent'}`}
              style={{background: c}}
              data-testid={`folder-color-${c}`}
            />
          ))}
        </div>
        {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
        <div className="flex justify-end gap-2 mt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-white/70 hover:bg-white/5"
            data-testid="folder-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-white disabled:opacity-50"
            style={{background: 'linear-gradient(90deg, #F96F51, #ff8a6b)'}}
            data-testid="folder-save"
          >
            {saving ? 'Saving…' : (folder ? 'Save' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
}
